/**
 * What a drag in the sidebar's "All spaces" folders means, as plain data, so it can be tested
 * without a browser. Drag ids: `folder:<key>` (a folder header), `space:<key>` (a space row),
 * `drop:<key>` (a folder's body, so empty, collapsed and sorted folders still take drops).
 */

export interface FolderLike {
  key: string;
  kind: "general" | "custom";
  sortMode: "custom" | "alpha" | "recent";
  spaceKeys: string[];
}

export type SidebarDrop =
  | { type: "reorderFolders"; folderKeys: string[] }
  | { type: "moveSpace"; spaceKey: string; folderKey: string; index: number | null }
  /** Placing a space by hand inside a sorted folder: needs the folder switched to custom order first. */
  | { type: "confirmCustomOrder"; spaceKey: string; folderKey: string; index: number }
  | null;

export const folderDragId = (key: string) => `folder:${key}`;
export const spaceDragId = (key: string) => `space:${key}`;
export const folderDropId = (key: string) => `drop:${key}`;

function parse(id: string): { kind: "folder" | "space" | "drop"; key: string } | null {
  const at = id.indexOf(":");
  const kind = id.slice(0, at);
  if (kind !== "folder" && kind !== "space" && kind !== "drop") return null;
  return { kind, key: id.slice(at + 1) };
}

function folderOf(folders: FolderLike[], spaceKey: string): FolderLike | undefined {
  return folders.find((s) => s.spaceKeys.includes(spaceKey));
}

/** The folder a drop target belongs to. */
function targetFolder(folders: FolderLike[], overId: string): FolderLike | undefined {
  const over = parse(overId);
  if (!over) return undefined;
  if (over.kind === "space") return folderOf(folders, over.key);
  return folders.find((s) => s.key === over.key);
}

export function resolveDrop(folders: FolderLike[], activeId: string, overId: string | null): SidebarDrop {
  const active = parse(activeId);
  if (!active || !overId || activeId === overId) return null;

  if (active.kind === "folder") {
    const target = targetFolder(folders, overId);
    const custom = folders.filter((s) => s.kind === "custom").map((s) => s.key);
    const from = custom.indexOf(active.key);
    // Dropping on General (always first) puts the folder first among the user's own.
    const to = target?.kind === "general" ? 0 : target ? custom.indexOf(target.key) : -1;
    if (from === -1 || to === -1 || from === to) return null;
    const next = [...custom];
    next.splice(to, 0, ...next.splice(from, 1));
    return { type: "reorderFolders", folderKeys: next };
  }

  if (active.kind !== "space") return null;
  const source = folderOf(folders, active.key);
  const target = targetFolder(folders, overId);
  if (!source || !target) return null;
  const over = parse(overId);

  if (target.sortMode !== "custom" && over?.kind === "space" && source.key === target.key) {
    return {
      type: "confirmCustomOrder",
      spaceKey: active.key,
      folderKey: target.key,
      index: target.spaceKeys.indexOf(over.key),
    };
  }
  // Sorted folders place the space by their sort; only Custom ones take a position.
  if (target.sortMode !== "custom" || over?.kind !== "space") {
    return source.key === target.key
      ? null
      : { type: "moveSpace", spaceKey: active.key, folderKey: target.key, index: null };
  }
  const index = target.spaceKeys.indexOf(over.key);
  if (source.key === target.key && index === source.spaceKeys.indexOf(active.key)) return null;
  return { type: "moveSpace", spaceKey: active.key, folderKey: target.key, index };
}

/** "Move up" / "Move down" in a Custom folder, from the menu (the keyboard path that needs no dragging). */
export function stepMove(folder: FolderLike, spaceKey: string, step: -1 | 1): SidebarDrop {
  const from = folder.spaceKeys.indexOf(spaceKey);
  const to = from + step;
  if (folder.sortMode !== "custom" || from === -1 || to < 0 || to >= folder.spaceKeys.length) return null;
  return { type: "moveSpace", spaceKey, folderKey: folder.key, index: to };
}

/** "Move up" / "Move down" for a Custom folder itself, from its own menu. General always stays first. */
export function stepMoveFolder(folders: FolderLike[], folderKey: string, step: -1 | 1): SidebarDrop {
  const custom = folders.filter((s) => s.kind === "custom").map((s) => s.key);
  const from = custom.indexOf(folderKey);
  const to = from + step;
  if (from === -1 || to < 0 || to >= custom.length) return null;
  const next = [...custom];
  next.splice(to, 0, ...next.splice(from, 1));
  return { type: "reorderFolders", folderKeys: next };
}

/** After a mouse or touch drag, drop focus from the grabbed row so its hover-only grip and actions don't stay on. A keyboard drag keeps focus. */
export function releasePointerFocus(activatorEvent: Event | null) {
  if (activatorEvent instanceof KeyboardEvent) return;
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
}
